import { Alert, Box, Button, Chip, Stack, Typography } from "@mui/material";
import { GlobalState } from "context/GlobalContext";
import React, { useEffect, useMemo, useState } from "react";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import RenderInputField from "components/AudienceDashboard/ReusableComponents/RenderInputField/RenderInputField";
import { filterColArry, clevertapLogsTableColumns, CLEVERTAP_FIXED_FIELD_VALUES, CLEVERTAP_DASHBOARD_OPTIONS } from "./data";
import { getColDefaultValue, isColumnDefault } from "components/EboStoreV4/data";
import { constraintsOptions, converAttrToDatagridCol } from "data/globalData";
import moment from "moment";
import DatagridWithApiPagination from "components/AudienceDashboard/ReusableComponents/DatagridWithApiPagination/DatagridWithApiPagination";
import { selectOptKeyName } from "components/ReplenishmentDashboard/data";
import { AudienceDashboardState } from "context/AudienceDashboardContext";
import axios from "axios";
import { AuthState } from "context/AuthContext";

const formatDateTime = (value) => (value ? moment(value).format("DD/MM/YY  HH:mm") : "");

const statusColor = (status) => {
	if (status === "Success") return "success";
	if (status === "Failed") return "error";
	return "warning";
};

const normalizeLogsResponse = (payload) => {
	if (!payload) {
		return { result: [], total_rows: 0 };
	}

	if (Array.isArray(payload.result)) {
		return {
			result: payload.result,
			total_rows: payload.total_rows ?? payload.result.length,
		};
	}

	if (Array.isArray(payload)) {
		return { result: payload, total_rows: payload.length };
	}

	return { result: [], total_rows: 0 };
};

const CleverTapAudience = () => {
	const { setSnackBar, snackBar, setSnackBarOpen, setLoadingOpen } = GlobalState();
	const { clevertapDrawerListItems, setClevertapDrawerListItems } = AudienceDashboardState();
	const { user } = AuthState();

	const defaultData = {
		...filterColArry?.reduce((acc, row) => {
			acc[row.key] = getColDefaultValue(row);
			return acc;
		}, {}),
		...CLEVERTAP_FIXED_FIELD_VALUES,
	};
	const [filterData, setFilterData] = useState({ ...defaultData });
	const [logs, setLogs] = useState([]);

	const columns = useMemo(
		() => [
			{
				field: "created_at",
				headerName: "Created",
				align: "center",
				headerAlign: "center",
				sortable: false,
				renderCell: ({ row, field }) => formatDateTime(row[field]),
				flex: 1,
				minWidth: 130,
			},
			...clevertapLogsTableColumns
				.filter(
					(col) =>
						col.constraints?.DISPLAY &&
						!["created_at", "updated_at", "id"].includes(col.key)
				)
				.map((col) => {
					if (col.key === "status") {
						return {
							...converAttrToDatagridCol(col),
							renderCell: ({ row, field }) => (
								<Chip
									label={row[field] || "—"}
									size="small"
									color={statusColor(row[field])}
									variant="outlined"
								/>
							),
						};
					}
					if (col.key === "dashboard") {
						return {
							...converAttrToDatagridCol(col),
							renderCell: ({ row, field }) => {
								const value = row[field];
								const option = CLEVERTAP_DASHBOARD_OPTIONS.find(
									(item) => item.key === value || item.name === value
								);
								return option?.name || value || "—";
							},
						};
					}
					if (col.key === "replace") {
						return {
							...converAttrToDatagridCol(col),
							renderCell: ({ row, field }) => (row[field] === true || row[field] === "true" ? "Yes" : "No"),
						};
					}
					return converAttrToDatagridCol(col);
				}),
			{
				field: "updated_at",
				headerName: "Updated",
				align: "center",
				headerAlign: "center",
				sortable: false,
				renderCell: ({ row, field }) => formatDateTime(row[field]),
				flex: 1,
				minWidth: 130,
			},
		],
		[]
	);

	const getDropdownOptions = async () => {
		setLoadingOpen(true);
		try {
			const res = await pythonVmInstance({
				url: `/audience/Clevertap_Audience`,
				headers: {
					"Content-Type": "application/json",
				},
				method: "get",
			});
			setClevertapDrawerListItems(res.data.result);
			setLoadingOpen(false);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Failed to load table options",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoadingOpen(false);
		}
	};

	useEffect(() => {
		if (!clevertapDrawerListItems) getDropdownOptions();
	}, []);

	const fetchLogs = async (limit, offset) => {
		try {
			const res = await pythonVmInstance.post(`/audience/get-clevertap-logs-list/${offset}?limit=${limit}`);
			return normalizeLogsResponse(res.data?.result ?? res.data);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,      
				message: "Failed to load CleverTap logs",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return { result: [], total_rows: 0 };
		}
	};

	const handleRunQueryClick = async (e) => {
		e.preventDefault();
		setLoadingOpen(true);
		const pendingFields = [];
		filterColArry?.forEach((attr) => {
			if (
				isColumnDefault(attr, filterData?.[attr?.key]) &&
				attr?.constraints?.[constraintsOptions.NOTNULL.key] === true
			) {
				pendingFields.push(attr);
			}
		});

		if (pendingFields?.length) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `Enter values for ${pendingFields.map((row) => row?.name).join(", ")}`,
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoadingOpen(false);
			return;
		}

		try {
			const res = await pythonVmInstance({
				url: `/audience/clevertap`,
				headers: {
					"Content-Type": "application/json",
				},
				method: "post",
				data: {
					...filterData,
					user_email: user.email,
				},
			});
			const { result = [] } = await fetchLogs(20, 0);
			setLogs(result);
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: res.data.message ?? "Executed successfully",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoadingOpen(false);
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: `Failed to run CleverTap push: ${error?.response?.data?.message || error.message || error}`,
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			setLoadingOpen(false);
		}
	};

	return (
		<Stack spacing={2} component="form" onSubmit={handleRunQueryClick}>
			<Stack spacing={2} pt={2}>
				<Box
					display="flex"
					columnGap={2}
					rowGap={2}
					flexWrap={{ xs: "wrap", sm: "nowrap" }}
					alignItems="flex-end">
					{filterColArry?.map((col) => {
						const column =
							col.key === "table_name" && clevertapDrawerListItems?.["table_name"]?.length
								? {
										...col,
										[selectOptKeyName]: {
											...col[selectOptKeyName],
											list:
												clevertapDrawerListItems?.["table_name"]?.map((row) => ({
													name: row,
													key: row,
												})) ?? [],
										},
									}
								: col;

						return (
							<Box key={column.key} flex={1} minWidth={{ xs: "100%", sm: 0 }}>
								<RenderInputField
									column={column}
									data={filterData}
									setData={setFilterData}
									selectOptObjName={selectOptKeyName}
									width="100%"
									longWidth="100%"
									disabled={false}
									disableLabel={false}
								/>
							</Box>
						);
					})}
				</Box>

				<Alert severity="info" sx={{ borderRadius: 2 }}>
					<Typography variant="body2">
						<strong>Note:</strong> This push uses{" "}
						<strong>Creator</strong> &quot;{CLEVERTAP_FIXED_FIELD_VALUES.creator}&quot; and{" "}
						<strong>CleverTap Email</strong> &quot;{CLEVERTAP_FIXED_FIELD_VALUES.email}&quot;.
					</Typography>
				</Alert>

				<Box>
					<Button variant="contained" sx={{ width: "150px" }} type="submit">
						Run
					</Button>
				</Box>
			</Stack>

			<Stack width="100%" height="60vh">
				<Typography variant="h6">Logs</Typography>
				<DatagridWithApiPagination
					setTableRows={setLogs}
					tableRows={logs}
					columns={columns}
					fetchTableRows={fetchLogs}
					recordsLimit={20}
					getRowId={(row) => row._id || row.id}
				/>
			</Stack>
		</Stack>
	);
};

export default CleverTapAudience;
